import React, { useState, useRef, useEffect } from 'react'
import { X, Shield, CheckCircle, AlertCircle } from 'lucide-react'

const MfaTestModal = ({ onClose }) => {
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null) // 'success' | 'error'
  const [error, setError] = useState('')
  const codeInputRef = useRef(null)

  useEffect(() => {
    setTimeout(() => codeInputRef.current?.focus(), 100)
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!code.trim() || code.length !== 6) {
      setError('Please enter a valid 6-digit code')
      return
    }

    setLoading(true)
    setError('')
    setResult(null)

    try {
      // Simulate MFA verification - in real implementation, this would call an API
      // For now, just simulate success/failure based on code
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      if (code === '123456') {
        setResult('error')
        setError('Invalid code. Please try again.')
      } else {
        setResult('success')
      }
    } catch (err) {
      setResult('error')
      setError('Test failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setCode(value)
    setError('')
    setResult(null)
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />
        
        <div className="inline-block w-full max-w-sm p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Test MFA
                </h3>
                <p className="text-sm text-gray-500">
                  Verify your authenticator app is working
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {!result && (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Enter the current 6-digit code from your authenticator app to test that MFA is working correctly.
              </p>

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Authentication code
                  </label>
                  <input
                    ref={codeInputRef}
                    type="text"
                    value={code}
                    onChange={handleCodeChange}
                    className="w-full px-3 py-3 text-center text-lg font-mono tracking-widest border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                    placeholder="000000"
                    maxLength={6}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                  />
                  {error && (
                    <div className="mt-2 flex items-center text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {error}
                    </div>
                  )}
                </div>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || code.length !== 6}
                    className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Testing...' : 'Test Code'}
                  </button>
                </div>
              </form>
            </>
          )}

          {/* Success Result */}
          {result === 'success' && (
            <div className="text-center">
              <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">
                MFA Test Successful!
              </h4>
              <p className="text-sm text-gray-600 mb-6">
                Your two-factor authentication is working correctly.
              </p>
              <button
                onClick={onClose}
                className="w-full px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Done
              </button>
            </div>
          )}

          {/* Error Result */}
          {result === 'error' && (
            <div className="text-center">
              <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">
                Test Failed
              </h4>
              <p className="text-sm text-gray-600 mb-6">
                {error}
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setResult(null)
                    setCode('')
                    setError('')
                    setTimeout(() => codeInputRef.current?.focus(), 100)
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Try Again
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MfaTestModal
